import * as XLSX from 'xlsx';
import { TableBlockData, TableCell } from './document.types';

/**
 * Intelligent delimiter detector for clipboard or text file data
 */
export function detectDelimiter(text: string): string {
  const firstLines = text.split(/\r?\n/).slice(0, 5).filter(line => line.trim().length > 0);
  if (firstLines.length === 0) return '\t';

  // Check if it's a markdown table
  if (firstLines.some(l => l.includes('|'))) {
    return '|';
  }

  let tabCount = 0;
  let commaCount = 0;
  let semiCount = 0;

  for (const line of firstLines) {
    tabCount += (line.match(/\t/g) || []).length;
    commaCount += (line.match(/,/g) || []).length;
    semiCount += (line.match(/;/g) || []).length;
  }

  if (tabCount >= commaCount && tabCount >= semiCount && tabCount > 0) return '\t';
  if (semiCount > commaCount && semiCount > 0) return ';';
  if (commaCount > 0) return ',';

  return '\t';
}

/**
 * Parses raw text (TSV from Excel/Sheets, CSV, Semicolon, or Markdown table)
 * into a structured TableBlockData
 */
export function parseDelimitedTextToTable(
  rawText: string,
  options?: { hasHeaderRow?: boolean; customDelimiter?: string }
): TableBlockData {
  if (!rawText || !rawText.trim()) {
    return createEmptyTable(2, 2);
  }

  const lines = rawText.trim().split(/\r?\n/);
  const delimiter = options?.customDelimiter || detectDelimiter(rawText);

  const rawGrid: string[][] = [];

  for (let line of lines) {
    line = line.trim();
    if (!line) continue;

    // Handle markdown table separator rows like |---|---|
    if (delimiter === '|' && /^\|?(\s*:?-+:?\s*\|)+\s*$/.test(line)) {
      continue;
    }

    let cols: string[] = [];
    if (delimiter === '|') {
      // Split by pipe and remove first/last empty elements if pipe bordered
      cols = line.split('|').map(c => c.trim());
      if (line.startsWith('|')) cols.shift();
      if (line.endsWith('|')) cols.pop();
    } else if (delimiter === ',') {
      // Standard CSV with quotes parsing
      cols = parseCsvLine(line, ',');
    } else if (delimiter === ';') {
      cols = parseCsvLine(line, ';');
    } else {
      // Tab or other whitespace delimiter
      cols = line.split(delimiter).map(c => c.trim());
    }

    if (cols.length > 0) {
      rawGrid.push(cols);
    }
  }

  if (rawGrid.length === 0) {
    return createEmptyTable(2, 2);
  }

  // Find max columns across all rows to make a rectangular grid
  const maxCols = Math.max(...rawGrid.map(row => row.length), 1);
  const totalRows = rawGrid.length;
  const hasHeaderRow = options?.hasHeaderRow ?? (totalRows > 1);

  const cells: TableCell[][] = [];
  const now = Date.now();

  for (let r = 0; r < totalRows; r++) {
    const rowCells: TableCell[] = [];
    const sourceRow = rawGrid[r] || [];

    for (let c = 0; c < maxCols; c++) {
      const content = (sourceRow[c] !== undefined ? sourceRow[c] : '').trim();
      const isHeader = r === 0 && hasHeaderRow;

      rowCells.push({
        id: `c_${r}_${c}_${now}_${Math.random().toString(36).substring(2, 6)}`,
        content,
        isHeader,
        bold: isHeader,
        alignment: isHeader ? 'center' : (isNaN(Number(content)) || content === '' ? 'left' : 'right'),
      });
    }
    cells.push(rowCells);
  }

  return {
    rows: totalRows,
    cols: maxCols,
    hasHeaderRow,
    hasHeaderCol: false,
    cells,
    borderStyle: 'academic',
  };
}

/**
 * Handles CSV quotes parsing accurately
 */
function parseCsvLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++; // skip next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

/**
 * Creates an empty default table
 */
export function createEmptyTable(rows: number = 3, cols: number = 3): TableBlockData {
  const cells: TableCell[][] = [];
  const now = Date.now();

  for (let r = 0; r < rows; r++) {
    const row: TableCell[] = [];
    for (let c = 0; c < cols; c++) {
      const isHeader = r === 0;
      row.push({
        id: `c_${r}_${c}_${now}_${Math.random().toString(36).substring(2, 6)}`,
        content: isHeader ? `Column ${c + 1}` : '',
        isHeader,
        bold: isHeader,
        alignment: 'center',
      });
    }
    cells.push(row);
  }

  return {
    rows,
    cols,
    hasHeaderRow: true,
    hasHeaderCol: false,
    cells,
    borderStyle: 'academic',
  };
}

/**
 * Parses uploaded spreadsheet file (.xlsx, .xls, .csv, .tsv, .txt)
 */
export async function parseExcelOrCsvFile(file: File): Promise<{
  sheetNames: string[];
  tables: Record<string, TableBlockData>;
  defaultTable: TableBlockData;
}> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const sheetNames = workbook.SheetNames;
  if (!sheetNames || sheetNames.length === 0) {
    throw new Error('No worksheets found in spreadsheet');
  }

  const tables: Record<string, TableBlockData> = {};

  for (const sheetName of sheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;

    // Convert sheet to 2D array of raw values
    const rawData = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' });

    if (!rawData || rawData.length === 0) {
      tables[sheetName] = createEmptyTable(2, 2);
      continue;
    }

    // Determine max columns
    const maxCols = Math.max(...rawData.map(r => (Array.isArray(r) ? r.length : 0)), 1);
    const totalRows = rawData.length;
    const now = Date.now();

    const cells: TableCell[][] = [];

    for (let r = 0; r < totalRows; r++) {
      const rowCells: TableCell[] = [];
      const sourceRow = rawData[r] || [];

      for (let c = 0; c < maxCols; c++) {
        const rawVal = sourceRow[c];
        let content = rawVal !== undefined && rawVal !== null ? String(rawVal).trim() : '';
        const isHeader = r === 0;

        rowCells.push({
          id: `c_${r}_${c}_${now}_${Math.random().toString(36).substring(2, 6)}`,
          content,
          isHeader,
          bold: isHeader,
          alignment: isHeader ? 'center' : (isNaN(Number(content)) || content === '' ? 'left' : 'right'),
        });
      }
      cells.push(rowCells);
    }

    tables[sheetName] = {
      rows: totalRows,
      cols: maxCols,
      hasHeaderRow: true,
      hasHeaderCol: false,
      cells,
      caption: `Data from ${file.name} - ${sheetName}`,
      borderStyle: 'academic',
    };
  }

  const firstSheet = sheetNames[0];
  const defaultTable = tables[firstSheet] || createEmptyTable(3, 3);

  return {
    sheetNames,
    tables,
    defaultTable,
  };
}
